import { Rank, Suit, type Card, type TableState } from "../gen/bazivan/v1/table_pb";
import { placeLabel } from "../game/place";
import { sortHand } from "../game/cards";

/**
 * وضعیتِ محلیِ میزِ حکم — پورتِ HokmOnlineMap.Apply.
 *
 * دو کارِ نقشه‌ی یونیتی همین‌جاست:
 *   • **چرخشِ صندلی:** سرور مطلق می‌شمارد؛ صحنه همیشه مرا صندلیِ ۰ می‌خواهد.
 *     local = (server − me + n) % n. چون n زوج است، هم‌تیمی همچنان روبه‌رو
 *     (صندلیِ ۲) می‌نشیند.
 *   • **دستِ حریف:** سرور فقط شمارش می‌دهد؛ صحنه پشتِ کارت می‌کشد، پس همان
 *     عدد کافی است.
 *
 * واحدها (تیم‌ها) به شماره‌ی **سرور** می‌مانند؛ چیزی که صحنه لازم دارد
 * «واحدِ من» است، نه چرخاندنِ همه‌ی واحدها.
 */
export type HokmLocal = {
  n: number;
  me: number;                 // صندلیِ من روی سرور
  myHand: Card[];             // مرتب‌شده، مثلِ دستی که روی میز چیده می‌شود
  counts: number[];           // شمارِ کارتِ هر صندلیِ **محلی** (۰ = من)
  trick: (Card | null)[];     // کارتِ زمینِ هر صندلیِ محلی
  trickLeader: number;        // محلی، ‎-۱ = زمین خالی
  ledSuit: Suit;
  turn: number;               // محلی
  hakem: number;              // محلی
  trumpChosen: boolean;
  trump: Suit;
  awaitingTrump: boolean;
  seatUnit: number[];         // واحدِ هر صندلیِ **محلی** (شماره‌ی سرور)
  units: number;
  teams: boolean;
  myUnit: number;
  tricksWon: number[];        // به شماره‌ی واحدِ سرور
  score: number[];
  tricksToWin: number;
  matchTarget: number;
  handOver: boolean;
  matchOver: boolean;
  // اطلاعاتِ کناری — همان Info
  names: string[];            // محلی
  bots: boolean[];
  avatars: string[];
  connected: boolean[];
  subscribers: boolean[];
  /** برچسبِ جا (شهر یا کشور) — خالی اگر بازیکن نگفته یا نشان نداده. */
  places: string[];
  carpet: string;
  myTurn: boolean;
  awaitingTrumpFromMe: boolean;
  turnRemaining: number;      // ثانیه؛ ۰ = بی‌مهلت
  /**
   * نمای تماشاچی: هیچ دستی مالِ من نیست.
   *
   * سرور my_hand را خالی می‌فرستد و me فقط لنگرِ چرخشِ صندلی‌هاست (۰). پس
   * صندلیِ ۰ هم مثلِ بقیه پشت‌ورو کشیده می‌شود و هیچ نوبتی «نوبتِ من» نیست —
   * وگرنه تماشاچی ساعتِ نوبت و دستِ خالیِ روشن می‌دید و فکر می‌کرد باید بازی
   * کند.
   */
  watching: boolean;
};

export const toLocal = (server: number, me: number, n: number) => n <= 0 ? 0 : ((server - me) % n + n) % n;
export const toServer = (local: number, me: number, n: number) => n <= 0 ? 0 : (local + me) % n;

export function emptyHokm(n = 4): HokmLocal {
  return {
    n, me: 0, myHand: [], counts: new Array(n).fill(0), trick: new Array(n).fill(null),
    trickLeader: -1, ledSuit: Suit.UNSPECIFIED, turn: 0, hakem: 0, trumpChosen: false,
    trump: Suit.UNSPECIFIED, awaitingTrump: false, seatUnit: new Array(n).fill(0), units: 2,
    teams: n === 4 || n === 6, myUnit: 0, tricksWon: [0, 0], score: [0, 0], tricksToWin: 7,
    matchTarget: 7, handOver: false, matchOver: false,
    names: new Array(n).fill(""), bots: new Array(n).fill(false), avatars: new Array(n).fill(""),
    connected: new Array(n).fill(true), subscribers: new Array(n).fill(false), places: new Array(n).fill(""), carpet: "",
    myTurn: false, awaitingTrumpFromMe: false, turnRemaining: 0, watching: false,
  };
}

/** نمای سرور را روی وضعیتِ محلی می‌نشانَد. */
export function applyState(ts: TableState, prev?: HokmLocal): HokmLocal {
  const n = ts.seats.length > 0 ? ts.seats.length : (ts.handCounts.length || 4);
  const me = ts.me;
  const st = prev && prev.n === n ? prev : emptyHokm(n);
  st.n = n; st.me = me;

  st.names = new Array(n).fill(""); st.bots = new Array(n).fill(false);
  st.avatars = new Array(n).fill(""); st.connected = new Array(n).fill(true);
  st.subscribers = new Array(n).fill(false); st.places = new Array(n).fill("");
  for (const seat of ts.seats) {
    const l = toLocal(seat.index, me, n);
    st.names[l] = seat.name; st.bots[l] = seat.bot; st.avatars[l] = seat.avatar;
    st.connected[l] = seat.connected || seat.bot; st.subscribers[l] = seat.subscriber;
    st.places[l] = placeLabel(seat);
  }

  st.watching = ts.watching;
  st.myHand = sortHand(ts.myHand);
  st.counts = new Array(n).fill(0);
  for (let s = 0; s < n; s++) st.counts[toLocal(s, me, n)] = s < ts.handCounts.length ? ts.handCounts[s]! : 0;
  // تماشاچی دستی ندارد، پس شمارِ صندلیِ لنگر همان است که سرور گفت.
  if (!st.watching) st.counts[0] = st.myHand.length;

  st.trick = new Array(n).fill(null);
  for (const t of ts.trick) if (t.card) st.trick[toLocal(t.seat, me, n)] = t.card;
  const lead = ts.trick.length > 0 ? ts.trick[0]! : null;
  st.trickLeader = lead ? toLocal(ts.leader, me, n) : -1;
  st.ledSuit = lead?.card?.suit ?? Suit.UNSPECIFIED;

  st.turn = toLocal(ts.turn, me, n);
  st.hakem = toLocal(ts.hakem, me, n);
  st.trumpChosen = ts.trumpChosen;
  // خالِ دستِ پیش روی سیم می‌مانَد: HokmState.NewHand فقط TrumpChosen را
  // false می‌کند و Trump را پاک نمی‌کند، و pbSuit هم هیچ‌وقت UNSPECIFIED
  // نمی‌دهد. این‌جا صافش می‌کنیم تا هیچ خواننده‌ای مجبور نباشد یادش بماند که
  // trumpChosen را هم بپرسد — یک بار فراموش‌کردنش یعنی خالِ دستِ پیش روی صفحه.
  st.trump = ts.trumpChosen ? ts.trump : Suit.UNSPECIFIED;
  st.awaitingTrump = ts.awaitingTrump;

  st.seatUnit = new Array(n).fill(0);
  for (let s = 0; s < n && s < ts.seatUnit.length; s++) st.seatUnit[toLocal(s, me, n)] = ts.seatUnit[s]!;
  st.units = ts.tricksWon.length || 2;
  st.teams = st.units < n;
  st.myUnit = st.seatUnit[0] ?? 0;
  st.tricksWon = [...ts.tricksWon];
  st.score = [...ts.score];
  st.tricksToWin = ts.tricksToWin || (n === 6 ? 5 : 7);
  st.matchTarget = ts.matchTarget || 7;
  st.handOver = ts.handOver;
  st.matchOver = ts.matchOver;
  st.carpet = ts.carpet;

  st.myTurn = !st.watching && ts.trumpChosen && !ts.handOver && st.turn === 0;
  st.awaitingTrumpFromMe = !st.watching && ts.awaitingTrump && st.hakem === 0;
  // سرور «باقی‌مانده» می‌فرستد، نه ساعتِ پایان (time.Until در سرور).
  st.turnRemaining = ts.turnDeadlineMs / 1000;
  return st;
}

/** واحدِ یک صندلیِ محلی. */
export const unitOf = (st: HokmLocal, local: number) => st.seatUnit[local] ?? 0;

/**
 * نوعِ بُرش برای انیمیشنِ چاقو — پورتِ HokmState.CutKindOf.
 * «none»: هم‌خال یا نه‌حکم. «cut»: اولین حکمِ این خال. «over»: حکمی که روی
 * حکمِ پیشین می‌نشیند — چه از آن بالاتر باشد چه نه.
 */
export type CutKind = "none" | "cut" | "over";

/** قوتِ کارت — پورتِ HokmState.Power. پروتکل ۱-پایه است (ACE=1…KING=13)، پس آس ۱۴ می‌شود و بقیه همان عدد. */
export const power = (c: Card) => (c.rank === Rank.ACE ? 14 : c.rank);

/**
 * هر حکمی که روی خالِ غیرِحکم بیفتد بریدن است — حتی اگر از حکمِ روی زمین
 * پایین‌تر باشد.
 *
 * پیش از این حکمِ پایین‌تر «none» می‌گرفت و هیچ انیمیشنی نداشت، با این
 * استدلال که زیرِ حکمِ حریف رفتن جشن ندارد. ولی سرِ میزِ چهارنفره بیشترِ
 * بریدن‌های دوم و سوم همین‌اند: بازیکن حکمش را می‌اندازد و صفحه هیچ نمی‌گوید،
 * انگار کارتی بازی نشده. حالا همه دیده می‌شوند و فرقشان فقط رنگِ نور است.
 *
 * `order`: چندمین بریدنِ این خال است — نورِ انیمیشن از همین رنگ می‌گیرد
 * (اولی آبی، از دومی به بعد قرمز). کارتِ در دست هنوز روی زمین ننشسته، پس
 * حکم‌های روی زمین شمرده می‌شوند و یکی هم خودش. سرِ «none» صفر است.
 *
 * `beaten`: بالاترین حکمِ روی زمین (‎-۱ اگر هیچ). فقط خبر است؛ برنده‌ی خال را
 * قاعده‌ی سرور تعیین می‌کند، نه این تابع.
 */
export function cutKindOf(st: HokmLocal, card: Card): { kind: CutKind; beaten: number; order: number } {
  const none = { kind: "none" as const, beaten: -1, order: 0 };
  if (!st.trumpChosen || st.trickLeader < 0) return none;
  if (card.suit !== st.trump || st.ledSuit === st.trump) return none;
  let beaten = -1, best = -1, trumps = 0;
  for (let l = 0; l < st.n; l++) {
    const c = st.trick[l];
    if (!c || c.suit !== st.trump) continue;
    trumps++;
    if (power(c) > best) { best = power(c); beaten = l; }
  }
  return { kind: beaten < 0 ? "cut" : "over", beaten, order: trumps + 1 };
}
